import { FunctionComponent, useState } from 'react';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import styles from './BodyMapSelector.module.css';

interface BodyMapSelectorProps {
  label?: string;
  value: string[];
  onChange: (selectedAreas: string[]) => void;
  gender?: 'male' | 'female' | 'neutral';
  multiple?: boolean;
  required?: boolean;
  hasNextButton?: boolean;
  onNext?: () => void;
}

interface BodyArea {
  id: string;
  name: string;
  x: number;
  y: number;
  width: number;
  height: number;
}

const bodyAreas: { front: BodyArea[]; back: BodyArea[] } = {
  front: [
    { id: 'head-front', name: 'Head', x: 140, y: 20, width: 120, height: 100 },
    { id: 'neck-front', name: 'Neck', x: 170, y: 120, width: 60, height: 40 },
    { id: 'chest', name: 'Chest', x: 120, y: 160, width: 160, height: 100 },
    { id: 'abdomen', name: 'Abdomen', x: 130, y: 260, width: 140, height: 100 },
    { id: 'pelvis', name: 'Pelvis', x: 130, y: 360, width: 140, height: 60 },
    { id: 'left-shoulder-front', name: 'Left Shoulder', x: 80, y: 160, width: 60, height: 60 },
    { id: 'right-shoulder-front', name: 'Right Shoulder', x: 260, y: 160, width: 60, height: 60 },
    { id: 'left-upper-arm-front', name: 'Left Upper Arm', x: 60, y: 220, width: 50, height: 80 },
    { id: 'right-upper-arm-front', name: 'Right Upper Arm', x: 290, y: 220, width: 50, height: 80 },
    { id: 'left-elbow-front', name: 'Left Elbow', x: 60, y: 300, width: 50, height: 40 },
    { id: 'right-elbow-front', name: 'Right Elbow', x: 290, y: 300, width: 50, height: 40 },
    { id: 'left-forearm-front', name: 'Left Forearm', x: 50, y: 340, width: 40, height: 80 },
    { id: 'right-forearm-front', name: 'Right Forearm', x: 310, y: 340, width: 40, height: 80 },
    { id: 'left-hand-front', name: 'Left Hand', x: 40, y: 420, width: 40, height: 60 },
    { id: 'right-hand-front', name: 'Right Hand', x: 320, y: 420, width: 40, height: 60 },
    { id: 'left-thigh-front', name: 'Left Thigh', x: 120, y: 420, width: 60, height: 120 },
    { id: 'right-thigh-front', name: 'Right Thigh', x: 220, y: 420, width: 60, height: 120 },
    { id: 'left-knee-front', name: 'Left Knee', x: 125, y: 540, width: 50, height: 50 },
    { id: 'right-knee-front', name: 'Right Knee', x: 225, y: 540, width: 50, height: 50 },
    { id: 'left-shin-front', name: 'Left Shin', x: 125, y: 590, width: 50, height: 100 },
    { id: 'right-shin-front', name: 'Right Shin', x: 225, y: 590, width: 50, height: 100 },
    { id: 'left-foot-front', name: 'Left Foot', x: 120, y: 690, width: 60, height: 40 },
    { id: 'right-foot-front', name: 'Right Foot', x: 220, y: 690, width: 60, height: 40 },
  ],
  back: [
    { id: 'head-back', name: 'Head (Back)', x: 140, y: 20, width: 120, height: 100 },
    { id: 'neck-back', name: 'Neck (Back)', x: 170, y: 120, width: 60, height: 40 },
    { id: 'upper-back', name: 'Upper Back', x: 120, y: 160, width: 160, height: 100 },
    { id: 'lower-back', name: 'Lower Back', x: 130, y: 260, width: 140, height: 100 },
    { id: 'buttocks', name: 'Buttocks', x: 130, y: 360, width: 140, height: 60 },
    { id: 'left-shoulder-back', name: 'Left Shoulder (Back)', x: 80, y: 160, width: 60, height: 60 },
    { id: 'right-shoulder-back', name: 'Right Shoulder (Back)', x: 260, y: 160, width: 60, height: 60 },
    { id: 'left-upper-arm-back', name: 'Left Upper Arm (Back)', x: 60, y: 220, width: 50, height: 80 },
    { id: 'right-upper-arm-back', name: 'Right Upper Arm (Back)', x: 290, y: 220, width: 50, height: 80 },
    { id: 'left-elbow-back', name: 'Left Elbow (Back)', x: 60, y: 300, width: 50, height: 40 },
    { id: 'right-elbow-back', name: 'Right Elbow (Back)', x: 290, y: 300, width: 50, height: 40 },
    { id: 'left-forearm-back', name: 'Left Forearm (Back)', x: 50, y: 340, width: 40, height: 80 },
    { id: 'right-forearm-back', name: 'Right Forearm (Back)', x: 310, y: 340, width: 40, height: 80 },
    { id: 'left-hand-back', name: 'Left Hand (Back)', x: 40, y: 420, width: 40, height: 60 },
    { id: 'right-hand-back', name: 'Right Hand (Back)', x: 320, y: 420, width: 40, height: 60 },
    { id: 'left-thigh-back', name: 'Left Thigh (Back)', x: 120, y: 420, width: 60, height: 120 },
    { id: 'right-thigh-back', name: 'Right Thigh (Back)', x: 220, y: 420, width: 60, height: 120 },
    { id: 'left-knee-back', name: 'Left Knee (Back)', x: 125, y: 540, width: 50, height: 50 },
    { id: 'right-knee-back', name: 'Right Knee (Back)', x: 225, y: 540, width: 50, height: 50 },
    { id: 'left-calf', name: 'Left Calf', x: 125, y: 590, width: 50, height: 100 },
    { id: 'right-calf', name: 'Right Calf', x: 225, y: 590, width: 50, height: 100 },
    { id: 'left-foot-back', name: 'Left Foot (Back)', x: 120, y: 690, width: 60, height: 40 },
    { id: 'right-foot-back', name: 'Right Foot (Back)', x: 220, y: 690, width: 60, height: 40 },
  ]
};

const BodyMapSelector: FunctionComponent<BodyMapSelectorProps> = ({
  label = 'Select affected areas',
  value = [],
  onChange,
  gender = 'neutral',
  multiple = true,
  required = false,
  hasNextButton = false,
  onNext
}) => {
  const [view, setView] = useState<'front' | 'back'>('front');
  const [hoveredArea, setHoveredArea] = useState<string | null>(null);

  const handleAreaClick = (areaId: string) => {
    if (multiple) {
      if (value.includes(areaId)) {
        onChange(value.filter(id => id !== areaId));
      } else {
        onChange([...value, areaId]);
      }
    } else {
      onChange([areaId]);
    }
  };

  const isAreaSelected = (areaId: string) => value.includes(areaId);

  const renderBodyOutline = () => {
    // Simplified body outline - in a real app, you'd use actual body SVG paths
    return (
      <g>
        {/* Head */}
        <ellipse cx="200" cy="70" rx="60" ry="50" fill="none" stroke="#e0e0e0" strokeWidth="2" />
        {/* Neck */}
        <rect x="180" y="120" width="40" height="40" fill="none" stroke="#e0e0e0" strokeWidth="2" />
        {/* Torso */}
        <rect x="130" y="160" width="140" height="260" rx="20" fill="none" stroke="#e0e0e0" strokeWidth="2" />
        {/* Arms */}
        <rect x="70" y="180" width="50" height="240" rx="25" fill="none" stroke="#e0e0e0" strokeWidth="2" />
        <rect x="280" y="180" width="50" height="240" rx="25" fill="none" stroke="#e0e0e0" strokeWidth="2" />
        {/* Legs */}
        <rect x="130" y="420" width="50" height="270" rx="25" fill="none" stroke="#e0e0e0" strokeWidth="2" />
        <rect x="220" y="420" width="50" height="270" rx="25" fill="none" stroke="#e0e0e0" strokeWidth="2" />
      </g>
    );
  };

  const currentAreas = bodyAreas[view];

  return (
    <div className={styles.container}>
      {label && (
        <label className={styles.label}>
          {label}
          {required && <span className={styles.required}>*</span>}
        </label>
      )}
      
      <div className={styles.viewToggle}>
        <button
          type="button"
          className={`${styles.viewButton} ${view === 'front' ? styles.active : ''}`}
          onClick={() => setView('front')}
        >
          Front View
        </button>
        <button
          type="button"
          className={`${styles.viewButton} ${view === 'back' ? styles.active : ''}`}
          onClick={() => setView('back')}
        >
          Back View
        </button>
      </div>

      <div className={styles.bodyMapContainer}>
        <svg 
          width="400" 
          height="730" 
          viewBox="0 0 400 730" 
          className={styles.bodyMap}
        >
          {renderBodyOutline()}
          
          {currentAreas.map(area => (
            <g key={area.id}>
              <rect
                x={area.x}
                y={area.y}
                width={area.width}
                height={area.height}
                className={`${styles.bodyArea} ${
                  isAreaSelected(area.id) ? styles.selected : ''
                } ${hoveredArea === area.id ? styles.hovered : ''}`}
                onClick={() => handleAreaClick(area.id)}
                onMouseEnter={() => setHoveredArea(area.id)}
                onMouseLeave={() => setHoveredArea(null)}
              />
              {isAreaSelected(area.id) && (
                <circle
                  cx={area.x + area.width / 2}
                  cy={area.y + area.height / 2}
                  r="8"
                  className={styles.selectedIndicator}
                />
              )}
            </g>
          ))}
        </svg>

        {hoveredArea && (
          <div className={styles.tooltip}>
            {currentAreas.find(a => a.id === hoveredArea)?.name}
          </div>
        )}
      </div>

      {value.length > 0 && (
        <div className={styles.selectedAreas}>
          <h4 className={styles.selectedTitle}>Selected areas:</h4>
          <div className={styles.selectedList}>
            {value.map(areaId => {
              const area = [...bodyAreas.front, ...bodyAreas.back].find(a => a.id === areaId);
              return area ? (
                <div key={areaId} className={styles.selectedItem}>
                  <span>{area.name}</span>
                  <button
                    type="button"
                    className={styles.removeButton}
                    onClick={() => handleAreaClick(areaId)}
                    aria-label={`Remove ${area.name}`}
                  >
                    ×
                  </button>
                </div>
              ) : null;
            })}
          </div>
        </div>
      )}
      
      {hasNextButton && (
        <button 
          type="button" 
          className={styles.nextButton} 
          onClick={onNext}
          disabled={required && value.length === 0}
        >
          Next
          <ChevronRightIcon />
        </button>
      )}
    </div>
  );
};

export default BodyMapSelector;